/**
 * 统一展示面（Display Surface）
 *
 * 供帮助 / 反馈 / 客服 / AI 等实用 SDK 共用：
 * - DisplayMode: link | new_tab | popup | redirect | modal
 * - 自动识别当前页面 URL / 域名 / 标题等上下文
 * - 参数拼装（手动 params + 自动 page context）
 * - 打开链接或 iframe 弹窗
 */

/** @typedef {'link'|'new_tab'|'popup'|'redirect'|'modal'|'iframe'} DisplayModeValue */

/**
 * 展示模式
 * - link / new_tab: 新标签打开
 * - popup: 居中小窗
 * - redirect: 当前页跳转
 * - modal / iframe: 全屏 iframe 遮罩弹层
 */
export const DisplayMode = Object.freeze({
  LINK: 'link',
  NEW_TAB: 'new_tab',
  POPUP: 'popup',
  REDIRECT: 'redirect',
  MODAL: 'modal',
  IFRAME: 'iframe',
});

/** 打开 URL 时默认注入的页面上下文字段（查询参数名） */
export const DEFAULT_PAGE_CONTEXT_QUERY_KEYS = Object.freeze([
  'page_url',
  'domain',
  'path',
  'title',
  'platform',
  'app_id',
  'language',
  'referrer',
]);

const SURFACE_IFRAME_ID = 'qbit-display-surface-iframe';
const SURFACE_OVERLAY_ID = 'qbit-display-surface-overlay';

let _activeMessageHandler = null;

/**
 * 采集当前页面上下文（浏览器环境）
 *
 * @param {Object} [overrides={}] - 覆盖字段（platform / appId / appName 等）
 * @returns {{
 *   page_url: string,
 *   domain: string,
 *   path: string,
 *   title: string,
 *   referrer: string,
 *   platform: string,
 *   app_id: string,
 *   app_name: string,
 *   language: string,
 *   screen: string,
 *   user_agent: string,
 * }}
 */
export function getPageContext(overrides = {}) {
  const hasWindow = typeof window !== 'undefined';
  const loc = hasWindow ? window.location : null;
  const doc = hasWindow ? document : null;
  const nav = hasWindow ? navigator : null;

  const platform = overrides.platform
    ?? overrides.platformId
    ?? 'qbit_open';
  const appId = overrides.appId
    ?? overrides.app_id
    ?? (loc ? loc.pathname : '');
  const appName = overrides.appName
    ?? overrides.app_name
    ?? (doc ? doc.title : '');

  return {
    page_url: overrides.page_url ?? overrides.pageUrl ?? (loc ? loc.href : ''),
    domain: overrides.domain ?? (loc ? loc.host : ''),
    path: overrides.path ?? (loc ? loc.pathname : ''),
    title: overrides.title ?? (doc ? doc.title : ''),
    referrer: overrides.referrer ?? (doc ? doc.referrer : ''),
    platform,
    app_id: appId,
    app_name: appName,
    language: overrides.language
      ?? (nav ? nav.language : 'zh'),
    screen: overrides.screen
      ?? (hasWindow && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : ''),
    user_agent: overrides.user_agent
      ?? overrides.userAgent
      ?? (nav ? nav.userAgent : ''),
  };
}

/**
 * 将对象转为查询参数（跳过 null/undefined/空字符串）
 * @param {Record<string, any>} params
 * @returns {URLSearchParams}
 */
export function toSearchParams(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (typeof value === 'object') {
      search.set(key, JSON.stringify(value));
      return;
    }
    search.set(key, String(value));
  });
  return search;
}

/**
 * 合并手动参数与页面上下文
 *
 * @param {Record<string, any>} [params={}] - 显式参数（优先级最高）
 * @param {Object} [options={}]
 * @param {boolean} [options.autoPageContext=true] - 是否自动注入页面上下文
 * @param {string[]} [options.pageContextKeys] - 要注入的上下文字段
 * @param {Object} [options.pageContext] - 覆盖 getPageContext 结果
 * @returns {Record<string, any>}
 */
export function mergeSurfaceParams(params = {}, options = {}) {
  const {
    autoPageContext = true,
    pageContextKeys = DEFAULT_PAGE_CONTEXT_QUERY_KEYS,
    pageContext: pageContextOverride,
  } = options;

  const merged = {};

  if (autoPageContext) {
    const ctx = pageContextOverride || getPageContext(options);
    pageContextKeys.forEach((key) => {
      if (ctx[key] !== undefined && ctx[key] !== null && ctx[key] !== '') {
        merged[key] = ctx[key];
      }
    });
  }

  Object.assign(merged, params || {});
  return merged;
}

/**
 * 构建带参数的完整 URL
 *
 * @param {string} baseUrl - 基础 URL（可含已有 query）
 * @param {Record<string, any>} [params={}] - 查询参数
 * @param {Object} [options={}] - 同 mergeSurfaceParams
 * @returns {string}
 */
export function buildUrl(baseUrl, params = {}, options = {}) {
  if (!baseUrl) {
    throw new Error('[DisplaySurface] baseUrl 是必填参数');
  }

  const merged = mergeSurfaceParams(params, options);
  const search = toSearchParams(merged);
  const query = search.toString();
  if (!query) return baseUrl;

  const hasQuery = baseUrl.includes('?');
  const joiner = hasQuery
    ? (baseUrl.endsWith('?') || baseUrl.endsWith('&') ? '' : '&')
    : '?';
  return `${baseUrl}${joiner}${query}`;
}

/**
 * 规范化展示模式（link → new_tab，iframe → modal）
 * @param {string} mode
 * @returns {string}
 */
export function normalizeDisplayMode(mode = DisplayMode.LINK) {
  const value = String(mode || DisplayMode.LINK).toLowerCase();
  if (value === DisplayMode.LINK || value === 'open') return DisplayMode.NEW_TAB;
  if (value === DisplayMode.IFRAME) return DisplayMode.MODAL;
  return value;
}

function cleanupSurfaceListeners() {
  if (_activeMessageHandler && typeof window !== 'undefined') {
    window.removeEventListener('message', _activeMessageHandler);
    _activeMessageHandler = null;
  }
}

/**
 * 关闭 modal / iframe 展示面
 */
export function closeSurface() {
  cleanupSurfaceListeners();
  if (typeof document === 'undefined') return;

  const overlay = document.getElementById(SURFACE_OVERLAY_ID);
  if (overlay) overlay.remove();

  const iframe = document.getElementById(SURFACE_IFRAME_ID);
  if (iframe) iframe.remove();
}

/**
 * 打开统一展示面（链接或弹窗）
 *
 * @param {Object} options
 * @param {string} options.url - 目标 URL（或 path）
 * @param {DisplayModeValue} [options.mode='link'] - 展示模式
 * @param {Record<string, any>} [options.params] - 额外查询参数
 * @param {boolean} [options.autoPageContext=true] - 自动填充当前页 URL 等
 * @param {string[]} [options.pageContextKeys] - 注入的上下文字段
 * @param {Object} [options.pageContext] - 覆盖页面上下文
 * @param {Object} [options.popupOptions] - popup 尺寸
 * @param {string} [options.popupName='qbit_surface'] - popup 窗口名
 * @param {string} [options.title] - modal 标题（可选）
 * @param {boolean} [options.showClose=true] - modal 是否显示关闭按钮
 * @param {string} [options.completeType='qbit_surface_complete'] - postMessage 完成类型
 * @param {string} [options.cancelType='qbit_surface_cancel'] - postMessage 取消类型
 * @param {Function} [options.onComplete] - 完成回调
 * @param {Function} [options.onCancel] - 取消回调
 * @param {Function} [options.onOpen] - 打开后回调
 * @returns {Window|HTMLElement|null}
 *
 * @example
 * openSurface({
 *   url: '/help',
 *   mode: 'modal',
 *   params: { topic: 'billing' },
 * });
 *
 * @example
 * openSurface({
 *   url: 'https://support.example.com',
 *   mode: 'link',
 *   params: { from: 'header' },
 * });
 */
export function openSurface(options = {}) {
  if (typeof window === 'undefined') {
    throw new Error('[DisplaySurface] openSurface 仅可在浏览器环境调用');
  }

  const {
    url,
    mode = DisplayMode.LINK,
    params = {},
    autoPageContext = true,
    pageContextKeys,
    pageContext,
    popupOptions = {},
    popupName = 'qbit_surface',
    title,
    showClose = true,
    completeType = 'qbit_surface_complete',
    cancelType = 'qbit_surface_cancel',
    onComplete,
    onCancel,
    onOpen,
    ...pageOverrides
  } = options;

  if (!url) {
    throw new Error('[DisplaySurface] url 是必填参数');
  }

  const finalUrl = buildUrl(url, params, {
    autoPageContext,
    pageContextKeys,
    pageContext,
    ...pageOverrides,
  });

  const normalized = normalizeDisplayMode(mode);

  switch (normalized) {
    case DisplayMode.NEW_TAB: {
      const win = window.open(finalUrl, '_blank', 'noopener,noreferrer');
      onOpen?.(win, finalUrl);
      return win;
    }

    case DisplayMode.REDIRECT: {
      onOpen?.(null, finalUrl);
      window.location.href = finalUrl;
      return null;
    }

    case DisplayMode.POPUP: {
      const width = popupOptions.width || 480;
      const height = popupOptions.height || 720;
      const left = (window.innerWidth - width) / 2 + (window.screenX || 0);
      const top = (window.innerHeight - height) / 2 + (window.screenY || 0);

      const popup = window.open(
        finalUrl,
        popupName,
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      if (popup && (onComplete || onCancel)) {
        const timer = setInterval(() => {
          if (popup.closed) {
            clearInterval(timer);
            cleanupSurfaceListeners();
            onCancel?.();
          }
        }, 500);

        cleanupSurfaceListeners();
        _activeMessageHandler = (event) => {
          try {
            const origin = new URL(finalUrl, window.location.href).origin;
            if (event.origin !== origin && event.origin !== window.location.origin) return;
          } catch {
            // ignore URL parse errors for relative paths already resolved
          }

          const data = event.data;
          if (data?.type === completeType) {
            clearInterval(timer);
            cleanupSurfaceListeners();
            onComplete?.(data.result ?? data.payload ?? data);
            popup.close();
          } else if (data?.type === cancelType) {
            clearInterval(timer);
            cleanupSurfaceListeners();
            onCancel?.(data);
            popup.close();
          }
        };
        window.addEventListener('message', _activeMessageHandler);
      }

      onOpen?.(popup, finalUrl);
      return popup;
    }

    case DisplayMode.MODAL: {
      closeSurface();

      const overlay = document.createElement('div');
      overlay.id = SURFACE_OVERLAY_ID;
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      if (title) overlay.setAttribute('aria-label', title);

      overlay.style.cssText = [
        'position:fixed',
        'inset:0',
        'z-index:999999',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'background:rgba(15,23,42,0.55)',
        'backdrop-filter:blur(4px)',
        'padding:16px',
        'box-sizing:border-box',
      ].join(';');

      const panel = document.createElement('div');
      panel.style.cssText = [
        'position:relative',
        'width:min(960px,100%)',
        'height:min(860px,92vh)',
        'background:#fff',
        'border-radius:16px',
        'overflow:hidden',
        'box-shadow:0 25px 50px -12px rgba(0,0,0,0.35)',
        'display:flex',
        'flex-direction:column',
      ].join(';');

      if (showClose || title) {
        const header = document.createElement('div');
        header.style.cssText = [
          'display:flex',
          'align-items:center',
          'justify-content:space-between',
          'gap:12px',
          'padding:12px 16px',
          'border-bottom:1px solid #e2e8f0',
          'background:#f8fafc',
          'flex-shrink:0',
        ].join(';');

        const titleEl = document.createElement('div');
        titleEl.textContent = title || '';
        titleEl.style.cssText = 'font:600 14px/1.4 system-ui,sans-serif;color:#0f172a;';
        header.appendChild(titleEl);

        if (showClose) {
          const closeBtn = document.createElement('button');
          closeBtn.type = 'button';
          closeBtn.setAttribute('aria-label', 'Close');
          closeBtn.textContent = '×';
          closeBtn.style.cssText = [
            'border:none',
            'background:transparent',
            'font-size:24px',
            'line-height:1',
            'cursor:pointer',
            'color:#64748b',
            'padding:0 4px',
          ].join(';');
          closeBtn.onclick = () => {
            closeSurface();
            onCancel?.();
          };
          header.appendChild(closeBtn);
        }

        panel.appendChild(header);
      }

      const iframe = document.createElement('iframe');
      iframe.id = SURFACE_IFRAME_ID;
      iframe.src = finalUrl;
      iframe.title = title || 'QBit Surface';
      iframe.allow = 'clipboard-write; fullscreen';
      iframe.style.cssText = 'flex:1;width:100%;border:none;background:#fff;';

      cleanupSurfaceListeners();
      _activeMessageHandler = (event) => {
        const data = event.data;
        if (data?.type === completeType) {
          closeSurface();
          onComplete?.(data.result ?? data.payload ?? data);
        } else if (data?.type === cancelType) {
          closeSurface();
          onCancel?.(data);
        }
      };
      window.addEventListener('message', _activeMessageHandler);

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          closeSurface();
          onCancel?.();
        }
      });

      panel.appendChild(iframe);
      overlay.appendChild(panel);
      document.body.appendChild(overlay);

      onOpen?.(overlay, finalUrl);
      return overlay;
    }

    default:
      throw new Error(`[DisplaySurface] 不支持的模式: ${mode}`);
  }
}

/**
 * 创建可复用的展示面助手（面向对象风格）
 */
export class DisplaySurfaceHelper {
  /**
   * @param {Object} [defaults={}] - 默认 openSurface 选项
   */
  constructor(defaults = {}) {
    this.defaults = defaults;
  }

  /**
   * @param {Object} [options={}]
   * @returns {Window|HTMLElement|null}
   */
  open(options = {}) {
    return openSurface({ ...this.defaults, ...options });
  }

  /**
   * @param {string} url
   * @param {Record<string, any>} [params]
   * @param {Object} [options]
   * @returns {string}
   */
  buildUrl(url, params = {}, options = {}) {
    return buildUrl(url, params, { ...this.defaults, ...options });
  }

  close() {
    closeSurface();
  }
}

/**
 * @param {Object} [defaults]
 * @returns {DisplaySurfaceHelper}
 */
export function createDisplaySurfaceHelper(defaults) {
  return new DisplaySurfaceHelper(defaults);
}

export default {
  DisplayMode,
  DEFAULT_PAGE_CONTEXT_QUERY_KEYS,
  getPageContext,
  toSearchParams,
  mergeSurfaceParams,
  buildUrl,
  normalizeDisplayMode,
  openSurface,
  closeSurface,
  DisplaySurfaceHelper,
  createDisplaySurfaceHelper,
};
